'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { ArrowLeft, Bird, TrendingDown, Activity, Skull, ShoppingCart, ArrowRightLeft, Layers, XCircle } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { KPICard, ChartCard, Card } from '@/components/ui/Cards';
import { MOCK_POPULATION_TREND, MOCK_FLOCK_SUMMARIES, MOVEMENT_TYPE_COLORS } from '../../components/birdRecordsData';

const mortalityTrend = [
  { date: '01 Aug', rate: 0.10, count: 5 },
  { date: '05 Aug', rate: 0.10, count: 5 },
  { date: '10 Aug', rate: 0.14, count: 7 },
  { date: '15 Aug', rate: 0.06, count: 3 },
  { date: '20 Aug', rate: 0.08, count: 4 },
  { date: '22 Aug', rate: 0.04, count: 2 },
  { date: '25 Aug', rate: 0.10, count: 5 },
  { date: '01 Sep', rate: 0.16, count: 8 },
  { date: '04 Sep', rate: 0.06, count: 3 },
  { date: '06 Sep', rate: 0.08, count: 4 },
];

const populationByFarm = [
  { name: 'Idudi Poultry Farm', birds: 24966 },
  { name: 'Kampala Demo Farm', birds: 6174 },
];

const populationByType = [
  { name: 'Broiler', value: 18818, color: '#2563eb' },
  { name: 'Layer', value: 14060, color: '#059669' },
  { name: 'Breeder', value: 2358, color: '#7c3aed' },
  { name: 'Local/Indigenous', value: 762, color: '#d97706' },
];

const movementDistribution = [
  { name: 'Mortality', value: 312, color: MOVEMENT_TYPE_COLORS.mortality },
  { name: 'Culling', value: 48, color: MOVEMENT_TYPE_COLORS.culling },
  { name: 'Transfer Out', value: 200, color: MOVEMENT_TYPE_COLORS.transfer_out },
  { name: 'Sale', value: 500, color: MOVEMENT_TYPE_COLORS.sale },
  { name: 'Transfer In', value: 200, color: MOVEMENT_TYPE_COLORS.transfer_in },
  { name: 'Adjustment', value: 2, color: MOVEMENT_TYPE_COLORS.positive_adjustment },
];

export default function BirdAnalyticsContent() {
  const [range, setRange] = useState<'7d' | '30d' | '3m'>('30d');

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Link href="/bird-records" className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground transition-colors">
            <ArrowLeft size={14} />
          </Link>
          <div>
            <h1 className="text-lg font-semibold text-foreground">Bird Records Analytics</h1>
            <p className="text-xs text-muted-foreground mt-0.5">Population trends, mortality analysis and movement insights</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {(['7d', '30d', '3m'] as const).map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${range === r ? 'bg-primary text-primary-foreground' : 'border border-border/60 text-muted-foreground hover:bg-muted/50'}`}
            >
              {r === '7d' ? '7 Days' : r === '30d' ? '30 Days' : '3 Months'}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
        <KPICard label="Current Population" value="31,140" sub="All active flocks" icon={Bird} iconBg="#eff6ff" iconColor="#2563eb" change={-1.1} changeLabel="vs last month" invertTrend />
        <KPICard label="Survival Rate" value="96.8%" sub="Cumulative all flocks" icon={Activity} iconBg="#f0fdf4" iconColor="#15803d" variant="success" />
        <KPICard label="Total Mortality" value="312" sub="1.2% mortality rate" icon={Skull} iconBg="#fef2f2" iconColor="#dc2626" change={8.3} changeLabel="vs last month" invertTrend variant="danger" />
        <KPICard label="Total Culled" value="48" sub="This period" icon={XCircle} iconBg="#fef2f2" iconColor="#b91c1c" />
        <KPICard label="Birds Sold" value="500" sub="Confirmed sales" icon={ShoppingCart} iconBg="#faf5ff" iconColor="#9333ea" />
        <KPICard label="Net Transfers" value="0" sub="In: 200 · Out: 200" icon={ArrowRightLeft} iconBg="#fff7ed" iconColor="#d97706" />
        <KPICard label="Avg Mortality Rate" value="0.09%" sub="Daily average" icon={TrendingDown} iconBg="#fef2f2" iconColor="#dc2626" />
        <KPICard label="Active Flocks" value={MOCK_FLOCK_SUMMARIES.filter(f => f.status === 'active').length} sub="Across all farms" icon={Layers} iconBg="#eff6ff" iconColor="#2563eb" />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Population Trend" subtitle="Closing population over time">
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={MOCK_POPULATION_TREND} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="popGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2563eb" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" strokeOpacity={0.5} />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(1)}k`} />
              <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid var(--border)' }} formatter={(v: number) => [v.toLocaleString(), '']} />
              <Area type="monotone" dataKey="closing" stroke="#2563eb" strokeWidth={2} fill="url(#popGrad)" name="Population" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Mortality Trend" subtitle="Daily mortality count and rate">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={mortalityTrend} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" strokeOpacity={0.5} />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid var(--border)' }} />
              <Bar dataKey="count" fill="#dc2626" radius={[3, 3, 0, 0]} name="Deaths" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Population by Farm */}
        <ChartCard title="Population by Farm" subtitle="Bird distribution across farms">
          <div className="space-y-2 mt-2">
            {populationByFarm.map((farm) => (
              <div key={farm.name}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-muted-foreground truncate">{farm.name}</span>
                  <span className="font-tabular font-semibold text-foreground ml-2">{farm.birds.toLocaleString()}</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-primary"
                    style={{ width: `${(farm.birds / 31140) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </ChartCard>

        {/* Population by Bird Type */}
        <ChartCard title="Population by Bird Type" subtitle="Distribution across bird categories">
          <ResponsiveContainer width="100%" height={160}>
            <PieChart>
              <Pie data={populationByType} cx="50%" cy="50%" innerRadius={40} outerRadius={65} paddingAngle={2} dataKey="value">
                {populationByType.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} formatter={(v: number) => [v.toLocaleString(), '']} />
            </PieChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 gap-1 mt-1">
            {populationByType.map((item) => (
              <div key={item.name} className="flex items-center gap-1.5 text-xs">
                <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                <span className="text-muted-foreground truncate">{item.name}</span>
                <span className="font-tabular font-medium text-foreground ml-auto">{item.value.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </ChartCard>

        {/* Movement Distribution */}
        <ChartCard title="Movement Distribution" subtitle="Breakdown of movement types">
          <div className="space-y-2 mt-2">
            {movementDistribution.map((item) => (
              <div key={item.name}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                    <span className="text-muted-foreground">{item.name}</span>
                  </div>
                  <span className="font-tabular font-semibold text-foreground">{item.value.toLocaleString()}</span>
                </div>
                <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${(item.value / 1262) * 100}%`, backgroundColor: item.color }}
                  />
                </div>
              </div>
            ))}
          </div>
        </ChartCard>
      </div>

      {/* Flock Performance Table */}
      <Card padding="sm">
        <div className="mb-3">
          <h3 className="text-sm font-semibold text-foreground">Flock Performance Summary</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Survival rates and mortality metrics by flock</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border/60">
                {['Flock', 'Type', 'Age', 'Initial', 'Current', 'Mortality', 'Culled', 'Survival %', 'Daily Mort %', 'Status'].map((h) => (
                  <th key={h} className="text-left py-2 px-2 text-muted-foreground font-medium whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MOCK_FLOCK_SUMMARIES.map((f) => (
                <tr key={f.flockId} className="border-b border-border/40 hover:bg-muted/30 transition-colors">
                  <td className="py-2 px-2 font-medium text-primary">{f.flockNumber}</td>
                  <td className="py-2 px-2 text-muted-foreground">{f.birdType}</td>
                  <td className="py-2 px-2 text-muted-foreground">{f.ageDays}d</td>
                  <td className="py-2 px-2 font-tabular text-foreground">{f.initialBirds.toLocaleString()}</td>
                  <td className="py-2 px-2 font-tabular font-semibold text-foreground">{f.currentBirds.toLocaleString()}</td>
                  <td className="py-2 px-2 font-tabular text-red-600">{f.mortality.toLocaleString()}</td>
                  <td className="py-2 px-2 font-tabular text-orange-600">{f.culled.toLocaleString()}</td>
                  <td className="py-2 px-2">
                    <span className={`font-tabular font-semibold ${f.survivalRate >= 97 ? 'text-green-600' : f.survivalRate >= 95 ? 'text-amber-600' : 'text-red-600'}`}>
                      {f.survivalRate.toFixed(1)}%
                    </span>
                  </td>
                  <td className="py-2 px-2 font-tabular text-muted-foreground">
                    {(f.mortalityRate / f.ageDays).toFixed(3)}%
                  </td>
                  <td className="py-2 px-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${f.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                      {f.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
